import React from "react";

import { useTranslation, useUserContext } from "utils";

import { COUNTRY_CODE } from "consts";

import { Drawer, Flex, Typography, WidgetVideo } from "components";

import { ResilienceScoreBreakdownSection } from "pages/app/resilience-score/components/resilience-score-context-drawer/ResilienceScoreBreakdownSection";
import ResilienceScoreSummaryCard from "pages/app/resilience-score/components/resilience-score-context-drawer/ResilienceScoreSummaryCard";
import ResilienceScoreTimelineCard from "pages/app/resilience-score/components/resilience-score-context-drawer/ResilienceScoreTimelineCard";
import {
  RESILIENCE_SCORE_EXPLAINER_VIDEO_VIMEO_URL,
  buildResilienceScoreDisplayYears,
} from "pages/app/resilience-score/utils/ResilienceScoreContextDrawerUtils";
import { FarmResilienceApiResponse, buildResilienceScoreViewModel } from "pages/app/resilience-score/utils/ResilienceScoreUtils";

type ResilienceScoreContextDrawerProps = {
  visible: boolean;
  onClose: () => void;
  selectedYear: number;
  farmResilienceScore: FarmResilienceApiResponse | null | undefined;
};

const ResilienceScoreContextDrawer: React.FC<ResilienceScoreContextDrawerProps> = ({ visible, onClose, selectedYear, farmResilienceScore }) => {
  const { t } = useTranslation(["resilienceScore"]);
  const { user } = useUserContext();
  const allowedCountries = [COUNTRY_CODE.GERMANY];
  const showVideo = !!user?.farm?.country && allowedCountries.includes(user.farm.country as COUNTRY_CODE);
  const current = buildResilienceScoreViewModel(farmResilienceScore, selectedYear);
  const previous = buildResilienceScoreViewModel(farmResilienceScore, selectedYear - 1);

  const years = buildResilienceScoreDisplayYears(farmResilienceScore, selectedYear);
  const timelineRows = years.map((year) => {
    const vm = buildResilienceScoreViewModel(farmResilienceScore, year);
    return {
      year,
      soilHealth: vm?.soilHealthScore ?? 0,
      waterManagement: vm?.waterManagementScore ?? 0,
      biodiversity: vm?.biodiversityScore ?? 0,
      overall: vm?.overallScore ?? 0,
      unknown: !vm,
    };
  });

  return (
    <Drawer
      className="resilience-score-context-drawer"
      dataTestId="resilience-score-context-drawer"
      visible={visible}
      onClose={onClose}
      title={t("resilienceScoreContextDrawer.title", {
        selectedYear,
      })}
    >
      <Flex vertical className="resilience-score-context-drawer-content" gap="2.5rem">
        <ResilienceScoreSummaryCard selectedYear={selectedYear} current={current} previous={previous} />
        <ResilienceScoreTimelineCard rows={timelineRows} maxScore={current?.maxScore ?? 10} />
        {showVideo && (
          <Flex vertical className="rsd-card rsd-meaning-card" gap="0.75rem">
            <Typography variant="paragraph-l" className="rsd-meaning-title">
              {t("resilienceScoreContextDrawer.meaning.title", "What this score means")}
            </Typography>
            <WidgetVideo
              vimeoUrlOrId={RESILIENCE_SCORE_EXPLAINER_VIDEO_VIMEO_URL}
              title={t("resilienceScoreContextDrawer.meaning.videoTitle", "Find why resilience matters")}
            />
          </Flex>
        )}
        <ResilienceScoreBreakdownSection
          selectedYear={selectedYear}
          farmResilienceScore={farmResilienceScore}
          years={years}
          maxScore={current?.maxScore ?? 10}
        />
      </Flex>
    </Drawer>
  );
};

export default ResilienceScoreContextDrawer;
